import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from './AuthContext';
import API, { authFetch } from './api';
import useScoringStatus from './useScoringStatus';
import { ScoringBanner } from './ScoringStatus';

const FIELD_LABELS = {
  name: 'Name', first_name: 'First name', last_name: 'Last name', email: 'Email',
  phone: 'Phone', notes: 'Notes', agent: 'Agent (broker assign)', address: 'Property address',
  record_type: 'Record type',
};

export default function ClientImport() {
  const { user } = useAuth();
  const canView = ['super_admin', 'broker', 'agent'].includes(user?.role);

  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);      // {headers, mapping, uncertain, preview, total_rows, template_applied, fields}
  const [mapping, setMapping] = useState({});
  const [dupMode, setDupMode] = useState('skip');
  const [templateName, setTemplateName] = useState('');
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(null);

  // The import returns as soon as the rows are committed; scoring continues in the
  // background. Poll so the founder can watch it finish (and retry anything that fails).
  const { status: scoringStatus, retry: retryScoring } = useScoringStatus();

  if (!canView) {
    return (
      <div className="min-h-screen bg-[#0A0E27] pt-8 pb-12"><div className="max-w-3xl mx-auto px-4">
        <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 text-center py-16">
          <h1 className="text-white text-2xl font-bold mb-2">Import</h1>
          <p className="text-[#B0BEC5]">Available to agents and brokers only.</p>
        </div></div></div>
    );
  }

  const onFile = async (f) => {
    setError(''); setResult(null); setPreview(null); setFile(f);
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append('file', f);
      const res = await authFetch(`${API}/api/import/preview`, { method: 'POST', body: fd });
      if (!res.ok) { const e = await res.json(); setError(e.detail || 'Could not read the file'); return; }
      const d = await res.json();
      setPreview(d); setMapping(d.mapping || {});
    } catch (e) { setError('Upload failed'); } finally { setBusy(false); }
  };

  const commit = async () => {
    if (!file) return;
    setBusy(true); setError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('mapping', JSON.stringify(mapping));
      fd.append('duplicate_mode', dupMode);
      fd.append('template_name', templateName.trim());
      const res = await authFetch(`${API}/api/import/commit`, { method: 'POST', body: fd });
      if (!res.ok) { const e = await res.json(); setError(e.detail || 'Import failed'); return; }
      setResult(await res.json());
    } catch (e) { setError('Import failed'); } finally { setBusy(false); }
  };

  const downloadErrors = () => {
    const rows = [['row', 'error'], ...result.errors.map(e => [e.row, e.message])];
    const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a'); a.href = url; a.download = 'import-errors.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  const copy = (txt, key) => { navigator.clipboard && navigator.clipboard.writeText(txt); setCopied(key); setTimeout(() => setCopied(null), 1500); };
  const setField = (field, header) => setMapping(m => ({ ...m, [field]: header || undefined }));

  return (
    <div className="min-h-screen bg-[#0A0E27] pt-6 pb-12">
      <div className="max-w-3xl mx-auto px-4 md:px-6">
        <Link to="/buyers" className="text-[#00D4FF] text-sm hover:text-[#00E676]">← Back to Buyers</Link>
        <h1 className="text-xl md:text-2xl font-bold text-white mt-1 mb-1">Import Clients & Sellers</h1>
        <p className="text-[#B0BEC5] text-sm mb-5">Upload a CSV of buyers, sellers, or both. Columns are auto-detected — adjust only where needed. Your mapping is saved for next time.</p>

        {error && <div className="bg-red-900/50 border border-red-700 text-red-300 px-4 py-2.5 rounded-lg mb-3 text-sm">{error}</div>}

        {/* Upload */}
        {!result && (
          <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 p-4 mb-4">
            <label className="block text-sm text-white font-medium mb-2">CSV file</label>
            <input type="file" accept=".csv,text/csv" onChange={(e) => onFile(e.target.files[0])}
              className="block w-full text-sm text-gray-300 file:mr-3 file:px-4 file:py-2 file:rounded-lg file:border-0 file:bg-[#00D4FF] file:text-[#0A0E27] file:font-semibold" />
            {busy && !preview && <div className="text-[#00D4FF] text-sm mt-2">Reading…</div>}
          </div>
        )}

        {/* Preview + mapping */}
        {preview && !result && (
          <>
            {preview.template_applied && (
              <div className="bg-[#1A2744] border border-[#00E676]/30 rounded-lg px-4 py-2 mb-3 text-sm text-[#B0BEC5]">
                ✓ Auto-applied your saved template “{preview.template_applied}”.
              </div>
            )}
            <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 p-4 mb-4">
              <h2 className="text-white font-semibold mb-1">Column mapping</h2>
              <p className="text-gray-400 text-xs mb-3">{preview.total_rows} rows. Fields flagged <span className="text-yellow-400">uncertain</span> — please confirm.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {preview.fields.map(field => (
                  <div key={field} className="flex items-center gap-2">
                    <span className={`text-xs w-32 shrink-0 ${preview.uncertain.includes(field) ? 'text-yellow-400' : 'text-gray-300'}`}>{FIELD_LABELS[field] || field}</span>
                    <select value={mapping[field] || ''} onChange={(e) => setField(field, e.target.value)}
                      className="flex-1 min-w-0 px-2 py-1.5 bg-[#0A0E27] border border-[#546E7A]/50 rounded text-white text-sm">
                      <option value="">— not mapped —</option>
                      {preview.headers.map(h => <option key={h} value={h}>{h}</option>)}
                    </select>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 p-4 mb-4 overflow-x-auto">
              <h2 className="text-white font-semibold mb-2 text-sm">Preview (first {preview.preview.length})</h2>
              <table className="w-full text-xs">
                <thead><tr className="text-gray-400 text-left border-b border-[#546E7A]/30">
                  <th className="py-1 pr-2">Type</th><th className="py-1 pr-2">Name</th><th className="py-1 pr-2">Email</th><th className="py-1 pr-2">Address</th><th className="py-1">Flags</th>
                </tr></thead>
                <tbody>
                  {preview.preview.map((r, i) => (
                    <tr key={i} className="border-b border-[#546E7A]/10">
                      <td className="py-1 pr-2 text-[#00D4FF]">{r.record_type}</td>
                      <td className="py-1 pr-2 text-white">{r.name || <span className="text-red-400">—</span>}</td>
                      <td className="py-1 pr-2 text-gray-300">{r.email}</td>
                      <td className="py-1 pr-2 text-gray-300">{r.address}</td>
                      <td className="py-1 text-yellow-400">{r.warnings.join(', ')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 p-4 mb-4 space-y-3">
              <div>
                <span className="text-white text-sm font-medium">On duplicate email:</span>
                <div className="flex gap-4 mt-1 text-sm">
                  <label className="flex items-center gap-1.5 text-gray-300"><input type="radio" checked={dupMode === 'skip'} onChange={() => setDupMode('skip')} className="accent-[#00D4FF]" /> Skip</label>
                  <label className="flex items-center gap-1.5 text-gray-300"><input type="radio" checked={dupMode === 'update'} onChange={() => setDupMode('update')} className="accent-[#00D4FF]" /> Update existing</label>
                </div>
              </div>
              <div>
                <label className="block text-white text-sm font-medium mb-1">Save this mapping as a template <span className="text-gray-500 text-xs">(optional, shared with your office)</span></label>
                <input type="text" value={templateName} onChange={(e) => setTemplateName(e.target.value)} placeholder="e.g. Follow Up Boss export"
                  className="w-full px-3 py-2 bg-[#0A0E27] border border-[#546E7A]/50 rounded-lg text-white text-sm" />
              </div>
              <button onClick={commit} disabled={busy}
                className="px-5 py-2 bg-[#00E676] text-[#0A0E27] font-semibold rounded-lg hover:bg-[#00C853] disabled:opacity-50 text-sm">
                {busy ? 'Importing…' : `Import ${preview.total_rows} rows`}
              </button>
            </div>
          </>
        )}

        {/* Results */}
        {result && (
          <div className="space-y-4">
            <div className="bg-[#1A2744] rounded-xl border border-[#00E676]/30 p-4">
              <h2 className="text-white font-semibold mb-2">Import complete</h2>
              <div className="grid grid-cols-3 gap-2 text-center">
                {[['Buyers', result.created.buyers], ['Sellers', result.created.sellers], ['Drafts', result.created.drafts]].map(([l, n]) => (
                  <div key={l} className="bg-[#0A0E27] rounded-lg p-2"><div className="text-[#00E676] text-2xl font-bold">{n}</div><div className="text-gray-400 text-xs">{l}</div></div>
                ))}
              </div>
              <div className="text-gray-400 text-xs mt-2">Skipped {result.skipped} · Updated {result.updated} · Errors {result.errors.length}{result.template_saved ? ` · Saved template “${result.template_saved}”` : ''}</div>
            </div>

            {/* Scoring continues after the import returns; this clears itself when done. */}
            <ScoringBanner status={scoringStatus} onRetry={retryScoring} label="clients" className="mb-0" />

            {result.errors.length > 0 && (
              <div className="bg-[#1A2744] rounded-xl border border-red-600/30 p-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-red-300 font-semibold text-sm">{result.errors.length} rows had errors</h3>
                  <button onClick={downloadErrors} className="text-[#00D4FF] text-xs hover:text-[#00E676]">Download error report</button>
                </div>
                <div className="max-h-48 overflow-y-auto text-xs space-y-1">
                  {result.errors.map((e, i) => <div key={i} className="text-gray-300">Row {e.row}: <span className="text-red-300">{e.message}</span></div>)}
                </div>
              </div>
            )}

            {result.invite_links.length > 0 && (
              <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 p-4">
                <h3 className="text-white font-semibold text-sm mb-2">Invite links ({result.invite_links.length})</h3>
                <p className="text-gray-500 text-xs mb-2">Email is stubbed — copy and send these so buyers can set up their account.</p>
                <div className="space-y-1.5 max-h-56 overflow-y-auto">
                  {result.invite_links.map((l, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <span className="text-gray-300 text-xs w-28 shrink-0 truncate">{l.name}</span>
                      <input readOnly value={l.url} className="flex-1 min-w-0 px-2 py-1 bg-[#0A0E27] border border-[#546E7A]/40 rounded text-[#00D4FF] text-xs" />
                      <button onClick={() => copy(l.url, i)} className="px-2 py-1 bg-[#00D4FF] text-[#0A0E27] rounded text-xs font-semibold shrink-0">{copied === i ? '✓' : 'Copy'}</button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex gap-3">
              <Link to="/buyers" className="px-4 py-2 bg-[#00E676] text-[#0A0E27] font-semibold rounded-lg text-sm">Go to Buyers</Link>
              <button onClick={() => { setResult(null); setPreview(null); setFile(null); }} className="px-4 py-2 bg-[#243B55] text-[#00D4FF] rounded-lg text-sm">Import another file</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
